import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Calculator, TrendingDown, TrendingUp, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Home-dashboard card (admins): this month's utilization and effective billed
// rate against the company's service-rate goals. Hidden when alerts are off.
export const RateGoalCard = ({ token }) => {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/service-rate/goal-status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [token]);

  if (!data || !data.enabled || data.total_hours === 0) return null;
  const alerts = data.alerts || [];
  const ok = alerts.length === 0;
  const Icon = ok ? TrendingUp : TrendingDown;

  return (
    <div className="ht-card p-4" data-testid="rate-goal-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${ok ? 'text-emerald-400' : 'text-amber-400'}`} />
          <h3 className="text-sm font-semibold text-white">Service rate goals · {data.month}</h3>
          <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${ok ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`} data-testid="rate-goal-status">
            {ok ? 'On track' : `${alerts.length} alert${alerts.length === 1 ? '' : 's'}`}
          </span>
        </div>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => navigate('/reports?report=service-rate')} data-testid="rate-goal-open-report">
          <Calculator className="w-3.5 h-3.5 mr-1" /> Open calculator
        </Button>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-2">
        <div data-testid="rate-goal-utilization">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Utilization</p>
          <p className={`text-base font-semibold ${data.utilization_pct < data.goals.target_utilization_pct ? 'text-amber-300' : 'text-emerald-300'}`}>{Number(data.utilization_pct).toFixed(0)}%</p>
          <p className="text-[11px] text-slate-500">goal {Number(data.goals.target_utilization_pct).toFixed(0)}% · {Number(data.billable_hours).toFixed(1)} of {Number(data.total_hours).toFixed(1)} h billable</p>
        </div>
        <div data-testid="rate-goal-effective">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Effective billed rate</p>
          <p className={`text-base font-semibold ${data.effective_rate != null && data.target_rate != null && data.effective_rate < data.target_rate ? 'text-amber-300' : 'text-white'}`}>{data.effective_rate == null ? '—' : `${money(data.effective_rate)}/h`}</p>
          <p className="text-[11px] text-slate-500">from job rates on closed sessions</p>
        </div>
        <div data-testid="rate-goal-target">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Target rate</p>
          <p className="text-base font-semibold text-emerald-300">{data.target_rate == null ? 'Enter costs' : `${money(data.target_rate)}/h`}</p>
          <p className="text-[11px] text-slate-500">{data.inputs_month ? `break-even ${money(data.break_even_rate)}/h · figures from ${data.inputs_month}` : 'no monthly figures saved yet'}</p>
        </div>
      </div>
      {alerts.length > 0 && (
        <ul className="space-y-1">
          {alerts.map((a) => (
            <li key={a.kind} className="text-xs text-amber-300 flex items-center gap-1.5" data-testid={`rate-goal-alert-${a.kind}`}>
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {a.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
